import type { SubmitTask, SubmitTaskState } from '@/modules/document/document-master/types/submit-task.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { computed, onMounted, reactive, toValue, type MaybeRefOrGetter, type Ref } from 'vue';

type SubmitRunnerOptions<TCode extends string> = {
  isLoading: Ref<boolean>;
  onForceClose: () => void;
  onSuccess?: () => void;
  onError?: (task: SubmitTask<TCode>, error: unknown) => void;
  autoRun?: boolean;
};

export function useSubmitRunner<TCode extends string>(
  tasks: MaybeRefOrGetter<SubmitTask<TCode>[]>,
  { isLoading, onForceClose, onSuccess, onError, autoRun = true }: SubmitRunnerOptions<TCode>
) {
  const states = reactive<Record<string, SubmitTaskState>>({});

  const items = computed(() =>
    toValue(tasks).map((task) => ({
      ...task,
      state: states[task.code] ?? 'loading',
    }))
  );

  const stopPending = () => {
    for (const task of toValue(tasks)) {
      if (states[task.code] === 'loading') {
        states[task.code] = 'stopped';
      }
    }
  };

  const run = async () => {
    isLoading.value = true;
    const list = toValue(tasks);

    for (const task of list) {
      states[task.code] = 'loading';
    }

    for (const task of list) {
      try {
        await task.handler();
        states[task.code] = 'success';
      } catch (error) {
        if (!ErrorUtil.isUnexpected(error) && !ErrorUtil.isBusinessError(error)) {
          continue;
        }

        states[task.code] = 'error';
        onError?.(task, error);

        if (task.required) {
          onForceClose();
          isLoading.value = false;
          stopPending();
          return;
        }
      }
    }

    isLoading.value = false;
    onSuccess?.();
  };

  if (autoRun) {
    onMounted(run);
  }

  return { items, run, states };
}
